// src/pages/admin/AdminProducts.js
// Fixes: error banner on list failure; retry; TableRowSkeleton; field errors in modal
import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { productsApi, unwrap, unwrapPage, getErrorMessage, getFieldErrors, getImageUrl, getImageUrlFallback } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { Button, Input, Textarea, Select, Modal, Badge, Spinner } from '../../components/ui';
import { ErrorBox, TableRowSkeleton } from '../../components/common/ErrorBoundary';

// ── Product Form Modal ─────────────────────────────────────────────────────────
function ProductModal({ product, categories, onSave, onClose }) {
  const { toast } = useToast();
  const isEdit    = Boolean(product);
  const [form, setForm] = useState({
    name:           product?.name           || '',
    description:    product?.description    || '',
    category:       product?.category       || '',
    price:          product?.price          || '',
    original_price: product?.original_price || '',
    stock:          product?.stock          ?? '',
    min_order:      product?.min_order      || 1,
    unit_type:      product?.unit_type      || '',
    is_featured:    product?.is_featured    ?? false,
    is_active:      product?.is_active      ?? true,
    tags:           product?.tags           || '',
  });
  const [imageFile,    setImageFile]    = React.useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
  const [imagePreview, setImagePreview] = React.useState(
    product?.image ? `${BACKEND_URL}${product.image}` : (product?.image_url || '')
  );
  const [errors,  setErrors]  = useState({});
  const [loading, setLoading] = useState(false);

  const set = f => e => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(p => ({ ...p, [f]: v }));
    setErrors(p => ({ ...p, [f]: '' }));
  };

  const handleSave = async () => {
    const errs = {};
    if (!form.name.trim() || form.name.trim().length < 3) errs.name = 'Name must be at least 3 chars.';
    if (!form.price || parseFloat(form.price) <= 0) errs.price = 'Price must be > ₹0.';
    if (form.stock === '' || parseInt(form.stock) < 0) errs.stock = 'Stock cannot be negative.';
    if (!form.category) errs.category = 'Select a category.';
    if (form.original_price && parseFloat(form.original_price) <= parseFloat(form.price))
      errs.original_price = 'Original price must exceed selling price.';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try {
      // Use FormData so we can upload image files
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (v === null || v === undefined || v === '') return;
        // Convert booleans to strings Django understands
        if (typeof v === 'boolean') { fd.append(k, v ? 'true' : 'false'); return; }
        fd.append(k, v);
      });
      if (imageFile) fd.append('image', imageFile);
      if (isEdit) {
        await productsApi.adminPatchFormData(product.id, fd);
        toast.success(`"${form.name}" updated.`);
      } else {
        await productsApi.adminCreateFormData(fd);
        toast.success(`"${form.name}" created.`);
      }
      onSave();
    } catch (err) {
      const fieldErrs = getFieldErrors(err);
      if (Object.keys(fieldErrs).length) setErrors(fieldErrs);
      else toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const catOptions = [
    { value:'', label:'Select category…' },
    ...categories.map(c => ({ value: c.id, label: c.name })),
  ];

  return (
    <Modal open title={isEdit ? 'Edit Product' : 'Add New Product'} onClose={onClose} maxWidth="max-w-2xl">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Input label="Product Name" value={form.name} onChange={set('name')}
            error={errors.name} required placeholder="e.g. Gold Sparklers 10cm"/>
        </div>
        <Select label="Category" options={catOptions} value={form.category}
          onChange={set('category')} error={errors.category} required/>
        <Input label="Selling Price (₹)" type="number" value={form.price}
          onChange={set('price')} error={errors.price} required min="1"/>
        <Input label="Original Price (₹)" type="number" value={form.original_price}
          onChange={set('original_price')} error={errors.original_price}
          placeholder="Strike-through price (optional)"/>
        <Input label="Stock" type="number" value={form.stock}
          onChange={set('stock')} error={errors.stock} required min="0"/>
        <Input label="Min Order Qty" type="number" value={form.min_order}
          onChange={set('min_order')} min="1"/>
        <Select
          label="Unit Type"
          value={form.unit_type}
          onChange={set('unit_type')}
          options={[
            { value:'',       label:'— None / Not specified —' },
            { value:'box',    label:'Box' },
            { value:'pkt',    label:'Packet (Pkt)' },
            { value:'piece',  label:'Piece' },
            { value:'set',    label:'Set' },
            { value:'dozen',  label:'Dozen' },
            { value:'bundle', label:'Bundle' },
            { value:'roll',   label:'Roll' },
          ]}
        />
        <div className="col-span-2">
          <Textarea label="Description" value={form.description} onChange={set('description')}
            rows={3} placeholder="Product details…"/>
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            Product Image
          </label>
          <div className="flex items-start gap-4">
            {/* Preview */}
            {imagePreview && (
              <img src={imagePreview}
                alt="preview"
                className="w-20 h-20 object-cover rounded-xl border border-gray-200 flex-shrink-0 bg-gray-50"/>
            )}
            <div className="flex-1">
              <label className="flex flex-col items-center justify-center w-full h-20
                border-2 border-dashed border-gray-300 rounded-xl cursor-pointer
                bg-gray-50 hover:bg-red-50 hover:border-red-300 transition-all duration-200 group">
                <div className="flex items-center gap-2 text-gray-400 group-hover:text-red-500 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                  </svg>
                  <span className="text-sm font-semibold">
                    {imageFile ? imageFile.name : 'Click to upload image'}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-1">JPG, PNG, WebP — max 5MB</p>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={e => {
                    const file = e.target.files[0];
                    if (!file) return;
                    // Validate file size (max 5MB)
                    if (file.size > 5 * 1024 * 1024) {
                      alert('Image must be under 5MB');
                      e.target.value = '';
                      return;
                    }
                    setImageFile(file);
                    // Create object URL for instant preview
                    const prev = URL.createObjectURL(file);
                    setImagePreview(prev);
                  }}
                />
              </label>
              {imageFile && (
                <button type="button"
                  onClick={() => { setImageFile(null); setImagePreview(''); }}
                  className="mt-1.5 text-xs text-red-500 hover:text-red-700 font-semibold">
                  ✕ Remove image
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="col-span-2">
          <Input label="Tags (comma-separated)" value={form.tags} onChange={set('tags')}
            placeholder="diwali, sparkler, kids"/>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.is_featured} onChange={set('is_featured')}
            className="w-4 h-4 accent-red-600 rounded"/>
          <span className="text-sm font-semibold text-gray-700">Featured Product</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={form.is_active} onChange={set('is_active')}
            className="w-4 h-4 accent-red-600 rounded"/>
          <span className="text-sm font-semibold text-gray-700">Active (visible)</span>
        </label>
      </div>
      <div className="flex gap-3 pt-5 mt-5 border-t border-gray-100">
        <Button variant="outline" className="flex-1" onClick={onClose} disabled={loading}>Cancel</Button>
        <Button className="flex-1" onClick={handleSave} loading={loading}>
          {isEdit ? 'Update Product' : 'Create Product'}
        </Button>
      </div>
    </Modal>
  );
}


// ── CSV/XLSX Import Modal ──────────────────────────────────────────────────────
const CSV_TEMPLATE_HEADERS = [
  'name','category','price','original_price','stock','min_order',
  'unit_type','description','tags','is_featured','is_active','image_url',
];
const CSV_SAMPLE_ROWS = [
  ['Gold Sparklers 10cm','sparklers','50','75','500','1','pkt','Premium gold sparklers','sparkler,kids,diwali','false','true',''],
  ['Sky King Rocket','rockets','150','200','100','1','box','High altitude rocket','rocket,sky,diwali','true','true',''],
  ['Flower Pot Classic','flower-pots','90','120','150','1','piece','Classic flower pot','flower,diwali','false','true',''],
];

function downloadTemplate() {
  const rows  = [CSV_TEMPLATE_HEADERS, ...CSV_SAMPLE_ROWS];
  const csv   = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
  const blob  = new Blob([csv], { type: 'text/csv' });
  const url   = URL.createObjectURL(blob);
  const a     = document.createElement('a');
  a.href      = url;
  a.download  = 'products_import_template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

function ImportModal({ onClose, onDone }) {
  const { toast }                   = useToast();
  const [file,      setFile]        = useState(null);
  const [dragging,  setDragging]    = useState(false);
  const [loading,   setLoading]     = useState(false);
  const [result,    setResult]      = useState(null);   // {created_count, skipped_count, created, skipped}
  const fileRef                     = React.useRef(null);

  const handleFile = f => {
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['csv','xlsx','xls'].includes(ext)) {
      toast.error('Only .csv or .xlsx files are supported.');
      return;
    }
    setFile(f);
    setResult(null);
  };

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res  = await productsApi.adminImport(fd);
      const data = res?.data || res;
      setResult(data);
      if (data.created_count > 0) {
        toast.success(`✅ ${data.created_count} product(s) imported!`);
        onDone();
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open title="📥 Import Products from CSV / XLSX" onClose={onClose} maxWidth="max-w-2xl">

      {/* Step 1 — download template */}
      <div className="bg-blue-50 border border-blue-200/60 rounded-2xl p-4 mb-5">
        <div className="flex items-start gap-3">
          <span className="text-2xl flex-shrink-0">📄</span>
          <div className="flex-1 min-w-0">
            <p className="font-black text-blue-900 text-sm mb-0.5">Step 1 — Download the template</p>
            <p className="text-blue-700/70 text-xs leading-relaxed mb-3">
              Fill in the template with your products. Required columns: <strong>name, category, price, stock</strong>.
              Category must match a slug or name from your Categories list.
            </p>
            <button onClick={downloadTemplate}
              className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700
                text-white font-bold text-xs px-4 py-2 rounded-xl transition-colors">
              ⬇ Download Template (.csv)
            </button>
          </div>
        </div>
      </div>

      {/* Step 2 — upload file */}
      <p className="font-black text-gray-700 text-sm mb-2">Step 2 — Upload your file</p>
      <div
        onClick={() => fileRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
        className={`relative border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer
          transition-all duration-200 mb-5
          ${dragging ? 'border-red-400 bg-red-50' : file ? 'border-green-400 bg-green-50' : 'border-gray-300 bg-gray-50 hover:border-red-300 hover:bg-red-50'}`}>
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
          onChange={e => handleFile(e.target.files[0])}/>
        {file ? (
          <div className="space-y-1">
            <p className="text-2xl">✅</p>
            <p className="font-black text-green-700 text-sm">{file.name}</p>
            <p className="text-green-600 text-xs">{(file.size/1024).toFixed(1)} KB — Ready to import</p>
            <button onClick={e => { e.stopPropagation(); setFile(null); setResult(null); }}
              className="text-red-500 hover:text-red-700 text-xs font-bold mt-1">✕ Remove</button>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-3xl">📂</p>
            <p className="font-bold text-gray-600 text-sm">Click to browse or drag & drop here</p>
            <p className="text-gray-400 text-xs">Supports .csv · .xlsx · .xls</p>
          </div>
        )}
      </div>

      {/* Column reference */}
      <details className="mb-5 bg-gray-50 rounded-2xl border border-gray-200">
        <summary className="px-4 py-3 text-xs font-black text-gray-600 uppercase tracking-widest cursor-pointer select-none">
          📋 Column Reference (click to expand)
        </summary>
        <div className="px-4 pb-4">
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-400 font-black uppercase tracking-widest border-b border-gray-200">
                  <th className="py-2 text-left pr-4">Column</th>
                  <th className="py-2 text-left pr-4">Required</th>
                  <th className="py-2 text-left">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  ['name',           '✅ Yes',  'Product name (min 2 chars)'],
                  ['category',       '✅ Yes',  'Category slug (e.g. sparklers) or name'],
                  ['price',          '✅ Yes',  'Selling price in ₹ (e.g. 150)'],
                  ['original_price', 'Optional','Strike-through price — must exceed price'],
                  ['stock',          'Optional','Units available (default: 0)'],
                  ['min_order',      'Optional','Min qty per order (default: 1)'],
                  ['unit_type',      'Optional','box / pkt / piece / set / dozen / bundle / roll'],
                  ['description',    'Optional','Product description text'],
                  ['tags',           'Optional','Comma-separated tags (e.g. diwali,kids)'],
                  ['is_featured',    'Optional','true or false (default: false)'],
                  ['is_active',      'Optional','true or false (default: true)'],
                  ['image_url',      'Optional','External image URL (https://...)'],
                ].map(([col, req, note]) => (
                  <tr key={col}>
                    <td className="py-2 pr-4 font-mono font-bold text-gray-700">{col}</td>
                    <td className={`py-2 pr-4 font-semibold ${req.includes('Yes') ? 'text-red-600' : 'text-gray-400'}`}>{req}</td>
                    <td className="py-2 text-gray-500">{note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </details>

      {/* Import result */}
      {result && (
        <div className="mb-5 space-y-3 animate-fade-in">
          {/* Summary badges */}
          <div className="flex gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1.5 bg-green-100 text-green-800
              text-sm font-black px-4 py-2 rounded-xl">
              ✅ {result.created_count} created
            </span>
            {result.skipped_count > 0 && (
              <span className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-800
                text-sm font-black px-4 py-2 rounded-xl">
                ⚠ {result.skipped_count} skipped
              </span>
            )}
          </div>
          {/* Skipped detail */}
          {result.skipped && result.skipped.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 max-h-40 overflow-y-auto">
              <p className="text-amber-800 font-black text-xs uppercase tracking-wider mb-2">
                Skipped rows — fix and re-import:
              </p>
              <ul className="space-y-1">
                {result.skipped.map((err, i) => (
                  <li key={i} className="text-amber-700 text-xs flex items-start gap-1.5">
                    <span className="flex-shrink-0">•</span>{err}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-3 pt-5 mt-2 border-t border-gray-100">
        <Button variant="outline" className="flex-1" onClick={onClose}>Close</Button>
        <Button className="flex-1" onClick={handleImport}
          loading={loading} disabled={!file || loading}>
          📥 Import Products
        </Button>
      </div>
    </Modal>
  );
}


// ── Inline stock editor ────────────────────────────────────────────────────────
function StockCell({ product, onUpdated }) {
  const { toast }             = useToast();
  const [editing, setEditing] = useState(false);
  const [value,   setValue]   = useState(String(product.stock));
  const [loading, setLoading] = useState(false);

  const save = async () => {
    const stock = parseInt(value);
    if (isNaN(stock) || stock < 0) { toast.error('Stock must be a non-negative number.'); return; }
    setLoading(true);
    try {
      await productsApi.adminUpdateStock(product.id, stock);
      toast.success(`Stock updated to ${stock}.`);
      onUpdated();
      setEditing(false);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (!editing) {
    const color = product.stock === 0 ? 'text-red-600' : product.stock <= 10 ? 'text-amber-600' : 'text-green-600';
    return (
      <button onClick={() => { setEditing(true); setValue(String(product.stock)); }}
        className={`text-sm font-bold hover:underline ${color}`}>
        {product.stock} units
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <input type="number" value={value} min="0" autoFocus
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => { if (e.key==='Enter') save(); if (e.key==='Escape') setEditing(false); }}
        className="w-20 border border-gray-200 rounded-lg py-1 px-2 text-sm
          focus:outline-none focus:ring-2 focus:ring-red-400"/>
      {loading ? <Spinner size="sm"/> : (
        <>
          <button onClick={save} className="text-green-600 text-xs font-bold hover:underline">✓</button>
          <button onClick={() => setEditing(false)} className="text-gray-400 text-xs hover:underline">✕</button>
        </>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function AdminProducts() {
  const { toast }              = useToast();
  const [products,   setProds] = useState([]);
  const [categories, setCats]  = useState([]);
  const [loading,    setLoad]  = useState(true);
  const [error,      setError] = useState(null);
  const [search,     setSearch]= useState('');
  const [modal,      setModal] = useState(null);
  const [delTarget,  setDel]   = useState(null);
  const [delLoad,    setDelL]  = useState(false);
  const [showImport, setImport] = useState(false);
  const [selected,   setSelected] = useState(new Set()); // selected product IDs
  const [bulkLoad,   setBulkLoad] = useState(false);

  const load = useCallback((signal) => {
    setLoad(true); setError(null);
    Promise.all([
      productsApi.adminList({ search }),
      productsApi.adminCategories(),
    ]).then(([p, c]) => {
      // Only update state if this request wasn't cancelled (component still mounted)
      if (signal && signal.aborted) return;
      setProds(unwrapPage(p).data);
      setCats(unwrap(c) || []);
      setError(null);
    }).catch(err => {
      if (signal && signal.aborted) return; // ignore errors from cancelled requests
      setError(err);
      setProds([]);
    }).finally(() => {
      if (signal && signal.aborted) return;
      setLoad(false);
    });
  }, [search]);

  useEffect(() => {
    // AbortController lets us cancel in-flight requests on cleanup/re-render
    const controller = new AbortController();
    // Small debounce for search typing only
    const t = setTimeout(() => load(controller.signal), 200);
    return () => {
      clearTimeout(t);
      controller.abort(); // cancel any in-flight request
    };
  }, [load]);

  const handleDelete = async () => {
    setDelL(true);
    try {
      await productsApi.adminDelete(delTarget.id);
      toast.success(`"${delTarget.name}" deactivated.`);
      setDel(null); load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDelL(false);
    }
  };

  // Toggle single checkbox
  const toggleSelect = (id) => setSelected(prev => {
    const s = new Set(prev);
    s.has(id) ? s.delete(id) : s.add(id);
    return s;
  });

  // Toggle all visible products
  const toggleAll = () => {
    if (selected.size === products.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(products.map(p => p.id)));
    }
  };

  // Bulk action handler
  const handleBulk = async (action) => {
    if (!selected.size) return;
    const label = {
      delete:      'permanently DELETE',
      deactivate:  'deactivate',
      activate:    'activate',
      feature:     'mark as featured',
      unfeature:   'remove from featured',
    }[action];
    if (!window.confirm(`${label.toUpperCase()} ${selected.size} product(s)? This cannot be undone.`)) return;
    setBulkLoad(true);
    try {
      const res = await productsApi.adminBulk({ ids: [...selected], action });
      toast.success(res?.message || res?.data?.message || `Done — ${selected.size} product(s) updated.`);
      setSelected(new Set());
      load(new AbortController().signal);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBulkLoad(false);
    }
  };

  // Hard delete single product
  const handleHardDelete = async () => {
    if (!delTarget) return;
    if (!window.confirm(`PERMANENTLY DELETE "${delTarget.name}"? This cannot be undone.`)) return;
    setDelL(true);
    try {
      await productsApi.adminHardDelete(delTarget.id);
      toast.success(`"${delTarget.name}" permanently deleted.`);
      setDel(null); load(new AbortController().signal);
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setDelL(false); }
  };

  return (
    <AdminLayout title="🎆 Products">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-3 items-center justify-between mb-5">
        <div className="relative">
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search products…"
            className="border border-gray-200 bg-white rounded-xl py-2.5 pl-9 pr-4 text-sm
              focus:outline-none focus:ring-2 focus:ring-red-400 w-64"/>
          <svg className="absolute left-3 top-3 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z"/>
          </svg>
        </div>
        <div className="flex items-center gap-2">
          {/* Import CSV/XLSX */}
          <button onClick={() => setImport(true)}
            className="flex items-center gap-2 bg-white border border-gray-200
              hover:border-blue-400 hover:bg-blue-50 text-gray-700 hover:text-blue-700
              font-bold text-sm px-4 py-2.5 rounded-xl transition-all duration-200 shadow-sm">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/>
            </svg>
            Import CSV / XLSX
          </button>
          <Button onClick={() => setModal('new')} icon="+" size="md">Add Product</Button>
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4 bg-blue-50 border border-blue-200
          rounded-2xl px-4 py-3 animate-fade-in">
          <span className="text-blue-700 font-black text-sm">{selected.size} selected</span>
          <div className="flex flex-wrap gap-2 ml-2">
            {[
              { action:'activate',   label:'✅ Activate',   cls:'bg-green-100 text-green-700 hover:bg-green-200' },
              { action:'deactivate', label:'⏸ Deactivate',  cls:'bg-amber-100 text-amber-700 hover:bg-amber-200' },
              { action:'feature',    label:'⭐ Feature',     cls:'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' },
              { action:'unfeature',  label:'☆ Unfeature',   cls:'bg-gray-100 text-gray-700 hover:bg-gray-200' },
              { action:'delete',     label:'🗑 Delete',      cls:'bg-red-100 text-red-700 hover:bg-red-200' },
            ].map(({action, label, cls}) => (
              <button key={action} onClick={() => handleBulk(action)} disabled={bulkLoad}
                className={`${cls} font-bold text-xs px-3 py-1.5 rounded-xl transition-colors disabled:opacity-50`}>
                {label}
              </button>
            ))}
          </div>
          <button onClick={() => setSelected(new Set())}
            className="ml-auto text-gray-400 hover:text-gray-600 text-xs font-bold">
            ✕ Clear
          </button>
        </div>
      )}

      {error && !load && <ErrorBox error={error} onRetry={load} className="mb-4"/>}

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-400 text-[11px] uppercase tracking-widest">
                <th className="px-4 py-3">
                  <input type="checkbox"
                    checked={products.length > 0 && selected.size === products.length}
                    onChange={toggleAll}
                    className="w-4 h-4 accent-red-600 cursor-pointer"/>
                </th>
                <th className="px-5 py-3 text-left">Product</th>
                <th className="px-5 py-3 text-left">Category</th>
                <th className="px-5 py-3 text-left">Price</th>
                <th className="px-5 py-3 text-left">Stock</th>
                <th className="px-5 py-3 text-left">Status</th>
                <th className="px-5 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                [...Array(6)].map((_,i) => <TableRowSkeleton key={i} cols={7}/>)
              ) : products.length === 0 && !error ? (
                <tr><td colSpan={7} className="py-12 text-center text-gray-400 text-sm">No products found</td></tr>
              ) : products.map(p => (
                <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4">
                    <input type="checkbox"
                      checked={selected.has(p.id)}
                      onChange={() => toggleSelect(p.id)}
                      className="w-4 h-4 accent-red-600 cursor-pointer"/>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={getImageUrl(p)}
                        alt={p.name}
                        onError={e => { e.target.src = getImageUrlFallback(); }}
                        className="w-10 h-10 rounded-xl object-cover bg-amber-50 flex-shrink-0"/>
                      <div>
                        <p className="font-bold text-gray-800 text-sm leading-tight">{p.name}</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {p.is_featured && <span className="text-[10px] text-yellow-600 font-bold">⭐ Featured</span>}
                          {p.unit_type && (
                            <span className="bg-amber-100 text-amber-700 text-[9px] font-black
                              px-1.5 py-0.5 rounded uppercase tracking-wide">
                              {{'box':'Box','pkt':'Pkt','piece':'Piece','set':'Set','dozen':'Dozen','bundle':'Bundle','roll':'Roll'}[p.unit_type] || p.unit_type}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-gray-500 text-sm">{p.category_name}</td>
                  <td className="px-5 py-4">
                    <span className="font-black text-gray-800">₹{p.price}</span>
                    {p.original_price && (
                      <span className="text-gray-400 line-through text-xs ml-1.5">₹{p.original_price}</span>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <StockCell product={p} onUpdated={load}/>
                  </td>
                  <td className="px-5 py-4">
                    <Badge color={p.is_active ? 'green' : 'gray'}>
                      {p.is_active ? 'Active' : 'Hidden'}
                    </Badge>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <button onClick={() => setModal(p)}
                        className="text-blue-600 hover:text-blue-800 font-bold text-xs transition-colors">
                        Edit
                      </button>
                      <button onClick={() => setDel(p)}
                        className="text-red-400 hover:text-red-600 font-bold text-xs transition-colors">
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <ProductModal
          product={modal === 'new' ? null : modal}
          categories={categories}
          onSave={() => { setModal(null); load(); }}
          onClose={() => setModal(null)}
        />
      )}
      {/* Delete confirmation with two options */}
      {Boolean(delTarget) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-sm w-full text-center">
            <div className="text-4xl mb-3">⚠️</div>
            <h3 className="font-black text-gray-900 text-lg mb-2">Remove Product?</h3>
            <p className="text-gray-500 text-sm mb-6">
              <strong>"{delTarget?.name}"</strong> — choose an action:
            </p>
            <div className="space-y-3">
              <button onClick={handleDelete} disabled={delLoad}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white font-black
                  py-3 rounded-2xl transition-colors disabled:opacity-50">
                ⏸ Deactivate (hide from store)
              </button>
              <button onClick={handleHardDelete} disabled={delLoad}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-black
                  py-3 rounded-2xl transition-colors disabled:opacity-50">
                🗑 Permanently Delete
              </button>
              <button onClick={() => setDel(null)} disabled={delLoad}
                className="w-full border border-gray-200 text-gray-600 hover:bg-gray-50
                  font-semibold py-3 rounded-2xl transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {showImport && (
        <ImportModal
          onClose={() => setImport(false)}
          onDone={() => { setImport(false); load(); }}
        />
      )}
    </AdminLayout>
  );
}
